/**
 * DirectMessagePage — Stage 2 PR 6. Minimal Slack-style 1:1 / group DM
 * thread view. Lives at /dm/:threadId and /dm/new (the "new" route
 * shows a user-picker; selecting a teammate opens or fetches the
 * existing thread and navigates to its id).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, MessageCircle, Send } from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { toast } from "sonner";


function _fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}


function NewDmPicker() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    v2Api.users()
      .then((u) => setUsers((u || []).filter((x) => x.is_active !== false)))
      .catch(() => {});
  }, []);

  const startDm = async (user) => {
    setOpening(true);
    try {
      const t = await v2Api.dms.openThread([user.id]);
      navigate(`/dm/${t.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open DM");
    } finally {
      setOpening(false);
    }
  };

  const q = filter.trim().toLowerCase();
  const visible = q
    ? users.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      )
    : users;

  return (
    <div className="max-w-md mx-auto py-12 px-4">
      <h1 className="text-xl font-semibold mb-1">Start a direct message</h1>
      <p className="text-sm text-slate-500 mb-4">Pick a teammate from your workspace.</p>
      <input
        autoFocus
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by name or email…"
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-500"
      />
      <div className="mt-4 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-96 overflow-y-auto">
        {visible.map((u) => {
          const initials = (u.name || u.email || "?")
            .split(/\s|@/).filter(Boolean).slice(0, 2)
            .map((s) => s[0].toUpperCase()).join("");
          return (
            <button
              key={u.id}
              type="button"
              disabled={opening}
              onClick={() => startDm(u)}
              className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-blue-50"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white text-[11px] font-semibold flex items-center justify-center">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
              </div>
            </button>
          );
        })}
        {visible.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-slate-400">No teammates match.</div>
        )}
      </div>
    </div>
  );
}


function MessageRow({ m, mine }) {
  const initials = (m.author_name || "?")
    .split(/\s|@/).filter(Boolean).slice(0, 2)
    .map((s) => s[0].toUpperCase()).join("");
  return (
    <div className={`flex gap-3 px-4 py-2 hover:bg-slate-50 ${mine ? "" : ""}`}>
      <div className="w-9 h-9 rounded bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-xs font-semibold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-slate-900 text-sm">{m.author_name || "Unknown"}</span>
          <span className="text-[10px] text-slate-400">{_fmtTime(m.created_at)}</span>
        </div>
        <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{m.body}</div>
      </div>
    </div>
  );
}


function ThreadView({ threadId }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      // List all threads to find this one — there isn't a single-fetch
      // endpoint today; participant count is tiny so this is fine.
      const all = await v2Api.dms.listThreads();
      const t = (all || []).find((x) => x.id === threadId);
      setThread(t || null);
      const msgs = await v2Api.dms.listMessages(threadId);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch (e) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => { refresh(); }, [refresh]);

  const msgCount = messages.length;
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgCount]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = composer.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const created = await v2Api.dms.sendMessage(threadId, body);
      setMessages((prev) => [...prev, created]);
      setComposer("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const title = (thread?.participants || [])
    .map((p) => p.name || p.email || "?")
    .join(", ");

  return (
    // Bounded height so the composer is always anchored to the bottom
    // of the viewport. The 3rem subtracts AppLayout's sticky 48px
    // header. Using `min-h-screen` here would let the page grow taller
    // than the viewport (long thread → composer pushed off-screen and
    // user has to scroll-up to type).
    <div className="h-[calc(100vh-3rem)] flex flex-col bg-white">
      <div className="px-6 py-3 border-b bg-white sticky top-0 z-10 shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-slate-400" />
          <h1 className="text-lg font-semibold text-slate-900 truncate">{title || "Direct message"}</h1>
        </div>
      </div>
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto py-2">
        {loading && (
          <div className="px-4 py-8 text-center text-slate-400 text-sm inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading messages…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="px-4 py-16 text-center text-sm text-slate-500">
            Quiet here. Say hello.
          </div>
        )}
        {messages.map((m) => <MessageRow key={m.id} m={m} mine={false} />)}
      </div>
      <form onSubmit={send} className="border-t border-slate-200 px-3 py-2 flex items-end gap-2 bg-slate-50 shrink-0">
        <textarea
          rows={1}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={onKey}
          placeholder="Message…"
          disabled={sending}
          className="flex-1 resize-none px-2 py-1.5 text-sm bg-white border border-slate-300 rounded focus:outline-none focus:border-blue-500 max-h-32"
        />
        <button
          type="submit"
          disabled={sending || !composer.trim()}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded bg-blue-600 text-white text-sm disabled:bg-slate-300 hover:bg-blue-700"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send
        </button>
      </form>
    </div>
  );
}


export default function DirectMessagePage() {
  const { threadId } = useParams();
  if (!threadId || threadId === "new") return <NewDmPicker />;
  return <ThreadView threadId={threadId} />;
}
